import { CloseOutlined, PlusOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Button, Col, Divider, Drawer, Flex, Input, Select, Spin, Typography } from "antd";
import dayjs from "dayjs";
import type { UIEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useClientBrowse } from "../../hooks/useClientBrowse";
import { useClientSearch } from "../../hooks/useClientSearch";
import { useResolveClient } from "../../hooks/useResolveClient";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { setAddClientView } from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import type { TClientForm } from "../../types/index.type";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import PickerListFooter from "../ClientPicker/PickerListFooter.component";
import styles from "./createnotemodal.module.scss";

const ClientSelector = () => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { isEditNote } = useAppSelector((state) => state.appState);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { referIndividualBy } = useSpecialityReference();

  // Pre-pagination this picker listed the user's own clients, so browse + search stay owned-scope.
  // Browsing reads the sidebar's sections (one source); search is a server overlay.
  const { query, results, loading, error, belowMinChars, onSearch, retry, reset } = useClientSearch("owned");
  const { browseClients, browseLoading, browseHasMore, browseError, ensureLoaded, loadMore, retryBrowse } =
    useClientBrowse("owned");

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Resolve the selected patient's name even if they're off-page (no in-memory list dependency).
  const { name: selectedClientName } = useResolveClient({
    id: recordingDetail?.client_id,
    uuid: recordingDetail?.client_uuid,
    name: recordingDetail?.client_name,
  });

  const isBrowsing = !query.trim();
  const sourceClients: TClientForm[] = isBrowsing ? browseClients : results;

  // Infinite scroll (dropdown popup + mobile drawer): nearing the bottom advances the shared
  // sidebar cursor. Search results are a single-page overlay.
  const onListScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (!isBrowsing || !browseHasMore || browseLoading) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) loadMore();
    },
    [isBrowsing, browseHasMore, browseLoading, loadMore],
  );

  const buildOption = useCallback((client: TClientForm) => {
    const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
    const derivedAgeFromDob =
      client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
    const rawAge =
      typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
    const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

    const secondaryDetails: string[] = [];
    if (formattedDob) {
      secondaryDetails.push(`DOB: ${formattedDob}`);
    } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
      secondaryDetails.push(`Age: ${displayAge}`);
    }
    if (client.mrn) {
      secondaryDetails.push(`MRN/Patient ID: ${client.mrn}`);
    }

    return {
      value: client.id,
      optionLabel: client.name,
      label: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            {client.name}{" "}
            {!client.is_owned && client.owner_name && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                - Shared by {client.owner_name}
              </span>
            )}
          </span>
          {secondaryDetails.length > 0 && (
            <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
              {secondaryDetails.join(" · ")}
            </span>
          )}
        </div>
      ),
      secondaryDetails,
      isOwned: client.is_owned,
      ownerName: client.owner_name,
      client,
    };
  }, []);

  const clientOptions = useMemo(() => sourceClients.map(buildOption), [sourceClients, buildOption]);

  const selectClient = useCallback(
    (client: TClientForm | undefined) => {
      dispatch(
        setRecordingDetail({
          ...recordingDetail,
          client_id: client?.id,
          client_uuid: client?.uuid ?? null,
          client_name: client?.name ?? null,
        }),
      );
    },
    [dispatch, recordingDetail],
  );

  const handleMobileClientSelect = useCallback(
    (client: TClientForm) => {
      selectClient(client);
      setIsMobileDrawerOpen(false);
      onSearch("");
    },
    [selectClient, onSearch],
  );

  const handleAddClientMobile = useCallback(() => {
    setIsMobileDrawerOpen(false);
    onSearch("");
    dispatch(setAddClientView(true));
  }, [dispatch, onSearch]);

  // Shared loading/error/empty flags for both layouts (browse-aware when the query is empty).
  const listLoading = isBrowsing ? browseLoading && browseClients.length === 0 : loading;
  const listError = isBrowsing ? browseError && browseClients.length === 0 : error;
  const retryList = isBrowsing ? retryBrowse : retry;
  const emptyMessage = listLoading
    ? null
    : listError
      ? "error"
      : belowMinChars
        ? `Type 2+ letters to search by name…`
        : query.trim()
          ? `No ${referIndividualBy.toLowerCase()}s match "${query.trim()}"`
          : `No ${referIndividualBy.toLowerCase()}s yet`;

  // Mobile: trigger button + bottom drawer
  if (isMobile) {
    return (
      <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
        <Typography.Text>{referIndividualBy}:</Typography.Text>
        <Button
          onClick={() => {
            ensureLoaded();
            setIsMobileDrawerOpen(true);
          }}
          className={`${styles[`${componentName}__modal-row-field`]} ${styles[`${componentName}__client-trigger`]}`}
          style={{
            width: "100%",
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "left",
            marginTop: 5,
            padding: "0 12px",
            borderColor: "var(--ant-color-border, #d9d9d9)",
          }}
        >
          <span
            style={{
              color: selectedClientName ? "inherit" : "var(--ant-color-text-placeholder, #bfbfbf)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedClientName || `Select a ${referIndividualBy.toLowerCase()}`}
          </span>
          <RightOutlined style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }} />
        </Button>

        <Drawer
          title={`Select ${referIndividualBy}`}
          placement="bottom"
          height="85%"
          open={isMobileDrawerOpen}
          onClose={() => {
            setIsMobileDrawerOpen(false);
            onSearch("");
          }}
          styles={{ body: { padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" } }}
          closeIcon={<CloseOutlined />}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
              backgroundColor: "var(--ant-color-bg-container, #fff)",
            }}
          >
            <Input
              prefix={<SearchOutlined style={{ color: "var(--ant-color-text-tertiary, #8c8c8c)" }} />}
              placeholder={`Search by name, DOB, or MRN/Patient ID...`}
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              allowClear
              size="large"
              style={{ borderRadius: 8 }}
              autoFocus
            />
          </div>

          {/* Scrollable client list (scrolls above the on-screen keyboard); paginates near the bottom. */}
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }} onScroll={onListScroll}>
            {listLoading ? (
              <div style={{ padding: "40px 16px", textAlign: "center" }} aria-busy="true">
                <Spin />
              </div>
            ) : listError ? (
              <div style={{ padding: "40px 16px", textAlign: "center", color: "#cf1322" }}>
                ⚠ Couldn't load — check your connection.{" "}
                <Button type="link" onClick={retryList}>
                  Retry
                </Button>
              </div>
            ) : clientOptions.length === 0 ? (
              <div
                style={{ padding: "40px 16px", textAlign: "center", color: "var(--ant-color-text-tertiary, #8c8c8c)" }}
              >
                <Typography.Text type="secondary">{emptyMessage}</Typography.Text>
              </div>
            ) : (
              <>
                {clientOptions.map((opt) => {
                  const clientName = opt.optionLabel || "";
                  const initials = clientName
                    .split(" ")
                    .filter((word: string) => word.length > 0)
                    .map((word: string) => word.charAt(0).toUpperCase())
                    .slice(0, 2)
                    .join("");
                  const isSelected = recordingDetail?.client_id === opt.value;

                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleMobileClientSelect(opt.client)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "8px",
                        borderBottom: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
                        backgroundColor: isSelected
                          ? "var(--ant-color-primary-bg, #e6f4ff)"
                          : "var(--ant-color-bg-container, #fff)",
                        cursor: "pointer",
                        minHeight: 48,
                      }}
                    >
                      <Avatar style={{ backgroundColor: getRandomAvatarColor(clientName), flexShrink: 0 }} size={40}>
                        {initials}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: isSelected ? 600 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {clientName}
                          {!opt.isOwned && opt.ownerName && (
                            <span
                              style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)", marginLeft: 6 }}
                            >
                              - Shared by {opt.ownerName}
                            </span>
                          )}
                        </div>
                        {opt.secondaryDetails.length > 0 && (
                          <div style={{ fontSize: 13, color: "var(--ant-color-text-tertiary, #8c8c8c)", marginTop: 2 }}>
                            {opt.secondaryDetails.join(" · ")}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "var(--ant-color-primary, #1677ff)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {isBrowsing && <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />}
              </>
            )}
          </div>

          {!isEditNote && (
            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
                backgroundColor: "var(--ant-color-bg-container, #fff)",
              }}
            >
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddClientMobile}
                block
                size="large"
                style={{ borderRadius: 8 }}
              >
                Add New {referIndividualBy}
              </Button>
            </div>
          )}
        </Drawer>
      </Col>
    );
  }

  // Desktop: server-backed Select.
  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Typography.Text>{referIndividualBy}:</Typography.Text>
      <Select
        value={recordingDetail?.client_id}
        listHeight={256}
        onChange={(value) => {
          const picked = clientOptions.find((o) => o.value === value)?.client;
          selectClient(picked);
        }}
        showSearch
        allowClear
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        filterOption={false}
        onSearch={onSearch}
        onDropdownVisibleChange={(open) => (open ? ensureLoaded() : reset())}
        onPopupScroll={onListScroll}
        loading={loading}
        notFoundContent={
          listLoading ? (
            <div style={{ textAlign: "center", padding: 8 }} aria-busy="true">
              <Spin size="small" />
            </div>
          ) : listError ? (
            <div style={{ padding: 8, color: "#cf1322" }}>
              ⚠ Couldn't load — check your connection.{" "}
              <Button type="link" size="small" style={{ padding: 0 }} onClick={retryList}>
                Retry
              </Button>
            </div>
          ) : (
            <div style={{ padding: 8, color: "#8c8c8c" }}>{emptyMessage}</div>
          )
        }
        labelRender={() => selectedClientName ?? null}
        optionRender={(client) => {
          const clientName = client.data?.optionLabel || "";
          const initials = clientName
            .split(" ")
            .filter((word) => word.length > 0)
            .map((word) => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join("");

          return (
            <Flex gap={10} align="center">
              <Avatar style={{ backgroundColor: getRandomAvatarColor(clientName) }} size="small">
                {initials}
              </Avatar>
              <Typography.Text>{client.label}</Typography.Text>
            </Flex>
          );
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            {isBrowsing && browseClients.length > 0 && (
              <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />
            )}
            {!isEditNote ? (
              <>
                <Divider style={{ margin: "4px 0" }} />
                <Flex gap={10}>
                  <Button type="text" icon={<PlusOutlined />} onClick={() => dispatch(setAddClientView(true))}>
                    Add {referIndividualBy}
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={clientOptions}
      />
    </Col>
  );
};

export default ClientSelector;
